/* ============================================================
   MobileNav.js -- phone navigation for FOX
   ------------------------------------------------------------
   Below 768px the header menu is hidden and navigation moves
   here: a bottom tab bar for the handful of dashboards someone
   opens on a phone, and a sheet listing every dashboard they
   have access to.

   The header menu was never a phone control. It wraps onto four
   rows at 393pt and consumes 42% of the viewport before any
   data appears, and its dropdowns opened on hover. Rather than
   shrink it, this replaces it where it does not fit and leaves
   it untouched everywhere else.

   Nothing here invents a permission model. Both surfaces are
   handed the same already-filtered section list the header menu
   builds from, so a page nobody can reach through the header
   cannot appear here either.
   ============================================================ */
import React from "react";
import { ChevronRight, Grid3x3, Home, Search, X } from "lucide-react";
import "./MobileNav.css";
import { useBodyScrollLock } from "./viewport";

/* Which dashboards earn a permanent slot in the tab bar, in order of who
   reaches for them standing up rather than sitting down. The first three the
   user actually has access to are used; everything else lives behind "All".

   Deliberately a fixed list rather than anything learned from usage: FOX
   records none, and a tab bar that moves around is worse than one that is
   merely imperfect. */
const TAB_PRIORITY = [
  "Aircraft Status",
  "Safety Cases",
  "Cancellations",
  "Ramp Activity Records",
  "Alerts",
  "Fleet Utilization Overview",
  "Training Hours",
];

const MAX_PRIMARY_TABS = 3;

/* Trims a dashboard name to something that fits under a 68px-wide tab without
   ellipsing. Only the names that actually overflow are listed; anything else
   is already short enough. */
const TAB_LABELS = {
  "Aircraft Status": "Fleet",
  "Safety Cases": "Safety",
  "Ramp Activity Records": "Ramp",
  "Fleet Utilization Overview": "Utilization",
  "Training Hours": "Hours",
};

export function primaryTabPages(sectionEntries) {
  const reachable = new Set();
  for (const [, pages] of sectionEntries) {
    for (const page of pages) reachable.add(page);
  }
  return TAB_PRIORITY.filter((page) => reachable.has(page)).slice(0, MAX_PRIMARY_TABS);
}

/* ---------------------------------------------------------------
   The sheet: every dashboard the user can open, grouped and
   searchable. Search matches the displayed label, because that is
   the name on the tab bar and on the home page -- someone hunting
   for "Fleet Reliability" should not have to know the page is
   stored as "Maintenance Surveillance".
   --------------------------------------------------------------- */
function AllDashboardsSheet({
  sectionEntries,
  currentPage,
  pageLabel,
  pageIcons,
  onSelect,
  onPrefetch,
  onClose,
}) {
  const [query, setQuery] = React.useState("");
  const searchRef = React.useRef(null);
  const sheetRef = React.useRef(null);
  useBodyScrollLock(true);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Focus the sheet itself rather than the search field: raising the keyboard
  // unprompted would cover the list the user came here to read.
  React.useEffect(() => {
    sheetRef.current?.focus();
  }, []);

  const needle = query.trim().toLowerCase();
  const groups = sectionEntries
    .map(([sectionName, pages]) => [
      sectionName,
      needle
        ? pages.filter((page) => pageLabel(page).toLowerCase().includes(needle))
        : pages,
    ])
    .filter(([, pages]) => pages.length > 0);

  const resultCount = groups.reduce((sum, [, pages]) => sum + pages.length, 0);

  return (
    <div className="mnav-sheet-root" role="dialog" aria-modal="true" aria-label="All dashboards">
      <button
        type="button"
        className="mnav-scrim"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
      />
      <div className="mnav-sheet" ref={sheetRef} tabIndex={-1}>
        <span className="mnav-grab" aria-hidden="true" />
        <div className="mnav-sheet-head">
          <span className="mnav-sheet-title">All dashboards</span>
          <button type="button" className="mnav-sheet-close" onClick={onClose} aria-label="Close">
            <X size={17} strokeWidth={2.2} />
          </button>
        </div>

        <div className="mnav-search">
          <Search size={17} strokeWidth={2} aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            placeholder="Search dashboards"
            aria-label="Search dashboards"
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button
              type="button"
              className="mnav-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <X size={15} strokeWidth={2.2} />
            </button>
          )}
        </div>

        <div className="mnav-sheet-body">
          {resultCount === 0 && (
            <p className="mnav-empty">No dashboard matches “{query.trim()}”.</p>
          )}
          {groups.map(([sectionName, pages]) => (
            <div className="mnav-group" key={sectionName}>
              <span className="mnav-group-label">{sectionName}</span>
              {pages.map((page) => {
                const PageIcon = pageIcons[page] || Grid3x3;
                return (
                  <button
                    type="button"
                    key={page}
                    className={`mnav-item${page === currentPage ? " active" : ""}`}
                    aria-current={page === currentPage ? "page" : undefined}
                    onPointerDown={() => onPrefetch?.(page)}
                    onClick={() => onSelect(page)}
                  >
                    <span className="mnav-item-icon">
                      <PageIcon size={16} strokeWidth={1.9} />
                    </span>
                    <span className="mnav-item-label">{pageLabel(page)}</span>
                    <ChevronRight size={16} strokeWidth={2.2} className="mnav-item-chev" />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   The tab bar. Home, up to three dashboards, and All.
   --------------------------------------------------------------- */
export default function MobileNav({
  sectionEntries,
  currentPage,
  setCurrentPage,
  pageLabel,
  pageIcons,
  alertCount = 0,
  /* Starts a page's chunk downloading before it is chosen. There is no hover
     on a phone, so the openings are narrower than on a desktop: the moment a
     finger lands on a tab, and the moment the sheet opens. Both still beat
     waiting until the tap completes. */
  onPrefetch,
}) {
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const primary = React.useMemo(() => primaryTabPages(sectionEntries), [sectionEntries]);

  // Leaving the page the sheet was opened from also closes the sheet, so a
  // back gesture cannot strand it open over a different dashboard.
  React.useEffect(() => {
    setSheetOpen(false);
  }, [currentPage]);

  const go = (page) => {
    setSheetOpen(false);
    setCurrentPage(page);
  };

  const warm = (page) => onPrefetch?.(page);

  return (
    <>
      <nav className="mnav-bar" aria-label="Primary">
        <button
          type="button"
          className={`mnav-tab${currentPage === "Home" ? " active" : ""}`}
          aria-current={currentPage === "Home" ? "page" : undefined}
          onClick={() => go("Home")}
        >
          <span className="mnav-tab-icon">
            <Home size={22} strokeWidth={1.9} />
          </span>
          Home
        </button>

        {primary.map((page) => {
          const PageIcon = pageIcons[page] || Grid3x3;
          const showBadge = page === "Alerts" && alertCount > 0;
          return (
            <button
              type="button"
              key={page}
              className={`mnav-tab${currentPage === page ? " active" : ""}`}
              aria-current={currentPage === page ? "page" : undefined}
              onPointerDown={() => warm(page)}
              onClick={() => go(page)}
            >
              <span className="mnav-tab-icon">
                <PageIcon size={22} strokeWidth={1.9} />
                {showBadge && (
                  <span className="mnav-badge">{alertCount > 9 ? "9+" : alertCount}</span>
                )}
              </span>
              {TAB_LABELS[page] || pageLabel(page)}
            </button>
          );
        })}

        <button
          type="button"
          className={`mnav-tab${sheetOpen ? " active" : ""}`}
          aria-expanded={sheetOpen}
          aria-haspopup="dialog"
          onClick={() => setSheetOpen((open) => !open)}
        >
          <span className="mnav-tab-icon">
            <Grid3x3 size={22} strokeWidth={1.9} />
          </span>
          All
        </button>
      </nav>

      {sheetOpen && (
        <AllDashboardsSheet
          sectionEntries={sectionEntries}
          currentPage={currentPage}
          pageLabel={pageLabel}
          pageIcons={pageIcons}
          onSelect={go}
          onPrefetch={warm}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </>
  );
}
